// pages/AppSettings.jsx
import React, { useEffect, useState } from "react";
import {
    getAppCustoms,
    updateAppCustomByKey,
} from "../processes/app_customization/app_customizaton";

export default function AppSettings() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadErr, setLoadErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const res = await getAppCustoms();
                const list = Array.isArray(res) ? res : res?.data || [];
                const withState = list.map((it) => ({
                    ...it,
                    _saving: false,
                    _error: "",
                }));
                setItems(withState);
            } catch (e) {
                setLoadErr(e?.message || "Failed to load settings");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const updateLocal = (key, patch) => {
        setItems((prev) =>
            prev.map((it) => (it.key === key ? { ...it, ...patch } : it))
        );
    };

    const handleSave = async (key, nextValue) => {
        const prev = items.find((x) => x.key === key);
        updateLocal(key, { value: nextValue, _saving: true, _error: "" });
        try {
            const updated = await updateAppCustomByKey(key, nextValue);
            const finalVal =
                updated?.value !== undefined ? updated.value : nextValue;
            updateLocal(key, {
                value: finalVal,
                _saving: false,
                updatedAt: updated?.updatedAt || new Date().toISOString(),
            });
        } catch (e) {
            updateLocal(key, {
                value: prev?.value,
                _saving: false,
                _error: e?.message || "Failed to save",
            });
        }
    };

    if (loading) return <div className="p-4">Loading…</div>;
    if (loadErr)
        return (
            <div className="p-4" style={{ color: "red" }}>
                {loadErr}
            </div>
        );

    return (
        <div className="p-4">
            <h2 style={{ marginBottom: 12 }}>App Settings</h2>
            <div style={{ display: "grid", gap: 12, maxWidth: 720 }}>
                {items.map((item) => (
                    <SettingRow key={item.key} item={item} onSave={handleSave} />
                ))}
            </div>
        </div>
    );
}

function SettingRow({ item, onSave }) {
    const { key, value, description, updatedAt, _saving, _error } = item;
    const valueType = typeof value;
    const inputKind =
        valueType === "boolean"
            ? "checkbox"
            : valueType === "number"
                ? "number"
                : "text";

    const [draft, setDraft] = useState(valueToInput(value, inputKind));
    useEffect(() => {
        setDraft(valueToInput(value, inputKind));
    }, [value, inputKind]);

    const saveIfChanged = () => {
        const parsed = inputToValue(draft, inputKind, value);
        if (parsed !== value) {
            onSave(key, parsed);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") e.currentTarget.blur();
    };

    return (
        <div
            style={{
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: 12,
                display: "grid",
                gridTemplateColumns: "1fr auto",
                alignItems: "center",
                gap: 8,
            }}
        >
            <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, wordBreak: "break-word" }}>{key}</div>

                {/* ✅ Show description */}
                {description && (
                    <div
                        style={{
                            color: "#4b5563",
                            fontSize: 13,
                            marginTop: 4,
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {description}
                    </div>
                )}

                {/* ✅ Show last updated time */}
                {updatedAt && (
                    <div
                        style={{
                            color: "#6b7280",
                            fontSize: 12,
                            marginTop: description ? 2 : 4,
                        }}
                    >
                        Last updated: {formatDate(updatedAt)}
                    </div>
                )}

                {_error && (
                    <div style={{ color: "red", fontSize: 12, marginTop: 6 }}>
                        {_error}
                    </div>
                )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {inputKind === "checkbox" ? (
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <input
                            type="checkbox"
                            checked={!!value}
                            onChange={(e) => onSave(key, e.target.checked)}
                            disabled={_saving}
                        />
                        <span style={{ fontSize: 14 }}>
              {value ? "Enabled" : "Disabled"}
            </span>
                    </label>
                ) : (
                    <input
                        type={inputKind}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={saveIfChanged}
                        onKeyDown={onKeyDown}
                        disabled={_saving}
                        style={{
                            minWidth: 220,
                            padding: "6px 10px",
                            border: "1px solid #d1d5db",
                            borderRadius: 8,
                        }}
                    />
                )}

                {_saving && (
                    <span style={{ fontSize: 13, color: "#6b7280" }}>Saving…</span>
                )}
            </div>
        </div>
    );
}

/* helpers */
function valueToInput(v, kind) {
    if (kind === "number") return v ?? 0;
    if (kind === "checkbox") return !!v;
    return v == null ? "" : String(v);
}
function inputToValue(input, kind, prevValue) {
    if (kind === "checkbox") return !!input;
    if (kind === "number") {
        const n = Number(input);
        return Number.isFinite(n)
            ? n
            : typeof prevValue === "number"
                ? prevValue
                : 0;
    }
    return input;
}
function formatDate(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}
